import _ from 'lodash'
import { hasCertifiableDimensions } from './Common'

type dimensionObject = {
    length: number
    width: number
    height: number
    density: number
    volume: number
    weight: weightObject
    dimensionBounds: dimensionCertificationsObject
    freightClass?: freightClassObject
}

type freightClassObject = {
    type: string
    class: string
    itemNumber: string
    subItemNumber: string
    // Flags for validation results
    exceededMin?: boolean
    exceededMax?: boolean
}

type weightObject = {
    net: number
    gross: number
    display: number
    tare: number
}

type unitsObject = {
    length: string
    volume: string
    density: string
    weight: string
}

type filesListObject = {
    clouds: string[];
    images: string[];
    xmls: string[];
    misc: string[];
}

type dimensionCertificationsObject = {
    lengthCertified: boolean;
    widthCertified: boolean;
    heightCertified: boolean;
    overallCertified: boolean;
}

type customFieldsObject = {
    CustomFields: {
        Field: any[]
    }
}

export class Cargo {
    cargoID: string = "";
    dimensionCode: string = "";
    dimensionCodeDescription: string = "";
    dimensionErrors: { code: string, description: string }[] = [];
    uploadCode: string = "";
    uploadCodeDescription: string = "";
    exportCode: string = "";
    exportCodeDescription: string = "";
    certification: any = {};
    dimensions: dimensionObject = {
        length: 0,
        width: 0,
        height: 0,
        density: 0,
        volume: 0,
        weight: {
            net: 0,
            gross: 0,
            display: 0,
            tare: 0
        },
        dimensionBounds: {
            lengthCertified: true,
            widthCertified: true,
            heightCertified: true,
            overallCertified: true
        },
        freightClass: {
            type: "",
            class: "",
            itemNumber: "",
            subItemNumber: ""
        }
    };
    customFields: customFieldsObject = {
        CustomFields: {
            Field: []
        }
    }

    units: unitsObject = {
        length: "",
        volume: "",
        density: "",
        weight: ""
    }

    fileList: filesListObject = {
        clouds: [],
        images: [],
        xmls: [],
        misc: []
    }
    cargoFolder: string = "";
    barcode: string = "";
    timeStamp: Date = new Date(Date.now());
    // Precise directory anchor (machine iso8601) used for paging older scans; empty for live Active-mode scans.
    iso8601: string = "";
    // Cached resolved thumbnail URL for the event-history row, avoids re-fetching on virtualization remount.
    thumbnailUrl: string = "";
    uploaded: boolean | null = null;
    exported: boolean | null = null;
    pendingBarcodeMatched: boolean = false;

    parseResponse(this: Cargo, unknownResponse: any, cmd: string) {
        if (!("Responses" in unknownResponse)) {
            console.log('Error: unknown response')
            if (cmd.includes("dimension")) {
                this.dimensionCode = "error";
            }
            if (cmd === "upload") {
                this.uploadCode = "error"
                this.uploaded = false
            }
            if (cmd === 'export') {
                this.exportCode = "error"
                this.exported = false
            }
            return;
        }
        if ("Dimension" in unknownResponse.Responses) {
            this.parseDimensionResponse(unknownResponse)
        }
        if ("Snapshot" in unknownResponse.Responses) {
            this.parseSnapshotResponse(unknownResponse)
        }
        if ("Upload" in unknownResponse.Responses) {
            this.parseUploadResponse(unknownResponse)
        }
        if ("Export" in unknownResponse.Responses) {
            this.parseExportResponse(unknownResponse)
        }
    }

    parseDimensionResponse(this: Cargo, dimensionResponse: any) {
        if (this.checkMachineResponseSuccessCode(dimensionResponse, 'Dimension')) {
            this.dimensionCode = "0"
            this.setCargoDetails(dimensionResponse)
        } else {
            this.dimensionCode = dimensionResponse?.['Responses']?.['Dimension']?.['code']
            this.dimensionCodeDescription = dimensionResponse?.['Responses']?.['Dimension']?.['description']
        }
    }

    parseSnapshotResponse(this: Cargo, snapshotResponse: any) {
        if (this.checkMachineResponseSuccessCode(snapshotResponse, 'Snapshot')) {
            // Extract cargoFolder from Xmls.Path if available
            const xmlPaths = snapshotResponse?.['Responses']?.['Snapshot']?.['Directory']?.['Xmls']?.['Path'];
            if (Array.isArray(xmlPaths) && xmlPaths.length > 0 && typeof xmlPaths[0] === 'string') {
                // Split at '/' and take the first part
                this.cargoFolder = xmlPaths[0].split('/')[0] || "";
            } else {
                this.cargoFolder = "";
            }
            this.setCargoSnapshots(snapshotResponse)
        } else {
            this.dimensionCode = snapshotResponse?.['Responses']?.['Snapshot']?.['code']
            this.dimensionCodeDescription = snapshotResponse?.['Responses']?.['Snapshot']?.['description']
        }
    }

    parseUploadResponse(this: Cargo, uploadResponse: any) {
        if (this.checkMachineResponseSuccessCode(uploadResponse, 'Upload')) {
            this.uploaded = true
            this.uploadCode = "0"
            this.cargoID = uploadResponse?.['Responses']?.['Upload']?.['Server']?.['cargoID']
        } else {
            this.uploaded = false
            this.uploadCode = uploadResponse?.['Responses']?.['Upload']?.['code']
            this.uploadCodeDescription = uploadResponse?.['Responses']?.['Upload']?.['description']
        }
    }

    parseExportResponse(this: Cargo, exportResponse: any) {
        if (this.checkMachineResponseSuccessCodeExport(exportResponse, 'Export')) {
            this.exported = true
            this.exportCode = "0"
        } else {
            this.exported = false
            const directCode = _.get(exportResponse, 'Responses.Export.code');
            const directDescription = _.get(exportResponse, 'Responses.Export.description');
            
            // If there's a non-success code directly on Export (like API_UNKNOWN), use it
            if (directCode && directCode !== "0") {
                this.exportCode = directCode;
                this.exportCodeDescription = directDescription || '';
            } else {
                // Fall back to EndpointResponse for HTTP-level errors
                this.exportCode = _.get(exportResponse, 'Responses.Export.EndpointResponse.StatusCode', "400");
                this.exportCodeDescription = _.get(exportResponse, 'Responses.Export.EndpointResponse.Reason', '');
            }
        }
    }

    parseListDirectoryResponse(this: Cargo, listDirectoryResponse: any) {
        this.setCargoDetailsFromDirectory(listDirectoryResponse)
    }

    checkMachineResponseSuccessCode(response: any, eventType: string) {
        return _.get(response, `Responses.${eventType}.code`) === "0"
    }

    checkMachineResponseSuccessCodeExport(response: any, eventType: string) {
        let statusCodeNum = parseInt(_.get(response, `Responses.${eventType}.EndpointResponse.StatusCode`))
        if ((_.get(response, `Responses.${eventType}.code`) === "0") && (statusCodeNum >= 200 && statusCodeNum < 400)) {
            return true
        } return false
    }

    setCargoDetails(this: Cargo, dimensionResponse: any) {
        let responseDims = dimensionResponse?.['Responses']?.['Dimension']?.['Info']?.['Dimensions']
        let responseUnits = dimensionResponse?.['Responses']?.['Dimension']?.['Info']?.['Units']

        if (!responseDims) {
            console.warn('No dimension data found in response');
            return;
        }

        this.barcode = responseDims['Barcode'] || ""
        this.dimensions.length = responseDims['Length'] || 0
        this.dimensions.width = responseDims['Width'] || 0
        this.dimensions.height = responseDims['Height'] || 0
        this.dimensions.density = responseDims['Density'] || 0
        this.dimensions.volume = responseDims['Volume'] || 0
        this.dimensions.weight.gross = responseDims['Weight']['Gross']
        this.dimensions.weight.net = responseDims['Weight']['Net']

        if (responseDims['Weight']) {
            this.dimensions.weight.gross = responseDims['Weight']['Gross'] || 0
            this.dimensions.weight.net = responseDims['Weight']['Net'] || 0
            if (responseDims['Weight']['Gross'] > responseDims['Weight']['Net']) {
                this.dimensions.weight.display = this.dimensions.weight.gross
            } else {
                this.dimensions.weight.display = this.dimensions.weight.net
            }
            this.dimensions.weight.tare = responseDims['Weight']['Tare'] || 0
        }

        // Handle FreightClass if present
        if (responseDims['FreightClass']) {
            this.dimensions.freightClass = {
                type: responseDims['FreightClass']['Type'] || "",
                class: responseDims['FreightClass']['Class'] || "",
                itemNumber: responseDims['FreightClass']['ItemNumber'] || "",
                subItemNumber: responseDims['FreightClass']['SubItemNumber'] || "",
                exceededMin: false,
                exceededMax: false
            }
        }

        this.units.density = responseUnits?.['Density'] || ""
        this.units.length = responseUnits?.['Length'] || ""
        this.units.weight = responseUnits?.['Weight'] || ""
        this.units.volume = responseUnits?.['Volume'] || ""
        
        // Check if Scanner exists and has Certified property before accessing it
        const scannerCertified = dimensionResponse?.['Responses']?.['Dimension']?.['Info']?.['Scanner']?.['Certified'];
        if (scannerCertified === 'null' && this.certification?.['LengthBounds'] && this.certification?.['WidthBounds'] && this.certification?.['HeightBounds']) {
            this.dimensions.dimensionBounds.overallCertified = false;
            this.dimensions.dimensionBounds.lengthCertified = hasCertifiableDimensions(this.dimensions.length, this.units.length, this.certification['LengthBounds'][0], this.certification['LengthBounds'][1]);
            this.dimensions.dimensionBounds.widthCertified = hasCertifiableDimensions(this.dimensions.width, this.units.length, this.certification['WidthBounds'][0], this.certification['WidthBounds'][1]);
            this.dimensions.dimensionBounds.heightCertified = hasCertifiableDimensions(this.dimensions.height, this.units.length, this.certification['HeightBounds'][0], this.certification['HeightBounds'][1]);
        }
    }

    setCargoDetailsFromDirectory(this: Cargo, listDirectoryResponse: any) {
        // Extract cargoFolder from Path array if available
        if (Array.isArray(listDirectoryResponse?.Path) && listDirectoryResponse.Path.length > 0 && typeof listDirectoryResponse.Path[0] === 'string') {
            this.cargoFolder = listDirectoryResponse.Path[0].split('/')[0] || "";
        } else {
            this.cargoFolder = "";
        }
        if (listDirectoryResponse?.Info) {
            if (listDirectoryResponse?.Info?.Dimensions?.Error) {
                this.dimensionCode = "error"
                const error = listDirectoryResponse?.Info?.Dimensions?.Error;
                // reset previous dimension errors for this cargo detail parsing
                this.dimensionErrors = [];
                if (error && typeof error === 'object') {
                    for (const [key, value] of Object.entries(error)) {
                        // This is temporary logic until we have a defined error response structure for translation purposes
                        if (value !== null && value !== undefined && value !== '' && typeof value === 'object') {
                            // Go one level deeper to get the actual error messages
                            const descriptions = Object.values(value)
                                .filter((innerValue: string | null | undefined) => innerValue !== null && innerValue !== undefined && innerValue !== '')
                                .map((innerValue: any) => String(innerValue));
                            if (descriptions.length > 0) {
                                const description = descriptions.length > 1
                                    ? '\n' + descriptions.join('\n\n')
                                    : descriptions[0];
                                this.dimensionErrors.push({ code: key, description });
                            }
                        }
                    }
                }
            } else {
                this.dimensionCode = "0"
            }
            let responseDims = listDirectoryResponse.Info?.Dimensions
            let responseUnits = listDirectoryResponse.Info?.Units

            this.barcode = responseDims?.Barcode ?? ""
            this.dimensions.length = responseDims?.Length
            this.dimensions.width = responseDims?.Width
            this.dimensions.height = responseDims?.Height
            this.dimensions.density = responseDims?.Density
            this.dimensions.volume = responseDims?.Volume
            this.dimensions.weight.gross = responseDims?.Weight?.Gross
            this.dimensions.weight.net = responseDims?.Weight?.Net
            if (responseDims?.Weight?.Gross > responseDims?.Weight?.Net) {
                this.dimensions.weight.display = this.dimensions.weight.gross
            } else {
                this.dimensions.weight.display = this.dimensions.weight.net
            }
            this.dimensions.weight.tare = responseDims?.Weight?.Tare

            // Handle FreightClass if present
            if (responseDims?.FreightClass) {
                this.dimensions.freightClass = {
                    type: responseDims['FreightClass']['Type'],
                    class: responseDims['FreightClass']['Class'],
                    itemNumber: responseDims['FreightClass']['ItemNumber'],
                    subItemNumber: responseDims['FreightClass']['SubItemNumber']
                }
            }

            this.units.density = responseUnits?.Density || ""
            this.units.length = responseUnits?.Length || ""
            this.units.weight = responseUnits?.Weight || ""
            this.units.volume = responseUnits?.Volume || ""
            
            // HACK: Temporarily treat all roll-through scans as certified (skip certification check)
            // The default values are already true, so no action needed

            // Check if Scanner exists and has Certified property before accessing it
            // const scannerCertified = listDirectoryResponse?.Info?.Scanner?.Certified;
            // if (scannerCertified === 'null') {
            //     // Only run hasCertifiableDimensions if this.units.length is not an empty string
            //     if (this.units.length) {
            //         this.dimensions.dimensionBounds.overallCertified = false;
            //         this.dimensions.dimensionBounds.lengthCertified = hasCertifiableDimensions(this.dimensions.length, this.units.length, this.certification['LengthBounds'][0], this.certification['LengthBounds'][1]);
            //         this.dimensions.dimensionBounds.widthCertified = hasCertifiableDimensions(this.dimensions.width, this.units.length, this.certification['WidthBounds'][0], this.certification['WidthBounds'][1]);
            //         this.dimensions.dimensionBounds.heightCertified = hasCertifiableDimensions(this.dimensions.height, this.units.length, this.certification['HeightBounds'][0], this.certification['HeightBounds'][1]);    
            //     }
            // }
        } else {
            this.dimensionCode = "error"
            this.dimensionCodeDescription = "Unknown Rollthrough error"
        }
        try {
            this.timeStamp = this.parseTimeFromFilePath(listDirectoryResponse?.name)
        } catch (Error) {
            this.timeStamp = new Date(Date.now());
            console.log("Error setting cargo time stamp")
        }

        if (typeof listDirectoryResponse?.iso8601 === 'string') {
            this.iso8601 = listDirectoryResponse.iso8601;
        }

        if (listDirectoryResponse?.Path) {
            let snapShotDirectory = listDirectoryResponse.Path

            // loop through the dimensionResponse, if the file extension ends in .jpg or .png, add it to the fileList.images
            for (let i = 0; i < snapShotDirectory.length; i++) {
                if (snapShotDirectory[i].endsWith('.jpg') || snapShotDirectory[i].endsWith('.png')) {
                    this.fileList.images.push(snapShotDirectory[i]);
                }
                // if file extension ends in .xml, add it to the fileList.xmls
                else if (snapShotDirectory[i].endsWith('.xml')) {
                    this.fileList.xmls.push(snapShotDirectory[i]);
                }
                // if file extension ends in .cloud, add it to the fileList.clouds
                else if (snapShotDirectory[i].endsWith('.cloud')) {
                    this.fileList.clouds.push(snapShotDirectory[i]);
                }
                // if file extension ends in .pdf, add it to the fileList.misc
                else if (snapShotDirectory[i].endsWith('.pdf')) {
                    this.fileList.misc.push(snapShotDirectory[i]);
                }
            }
            this.setMarkedImageOrder();
        }
    }

    setCargoSnapshots(this: Cargo, dimensionResponse: any) {
        let snapshotDirectory = dimensionResponse['Responses']['Snapshot']['Directory']

        _.has(snapshotDirectory, 'Clouds') ? this.fileList.clouds = snapshotDirectory['Clouds']['Path'] : this.fileList.clouds = []
        _.has(snapshotDirectory, 'Images') ? this.fileList.images = snapshotDirectory['Images']['Path'] : this.fileList.images = []
        _.has(snapshotDirectory, 'Misc') ? this.fileList.misc = snapshotDirectory['Misc']['Path'] : this.fileList.misc = []
        _.has(snapshotDirectory, 'Xmls') ? this.fileList.xmls = snapshotDirectory['Xmls']['Path'] : this.fileList.xmls = []

        this.setMarkedImageOrder();
        // xml time stamp logic
        try {
            let filePath = this.fileList.images[0];
            let timeStamp = filePath.split('/')[0];
            this.timeStamp = this.parseTimeFromFilePath(timeStamp);

        } catch {
            this.timeStamp = new Date(Date.now());
            console.log("Error setting cargo time stamp")
        }
    }

    setMarkedImageOrder(this: Cargo) {
        this.fileList.images = this.fileList.images.sort((a, b) => {
            if (a.includes("marked") && !b.includes("marked")) {
                return -1;
            } else if (!a.includes("marked") && b.includes("marked")) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    parseTimeFromFilePath(this: Cargo, timeStamp: string) {
        let date = new Date(Date.now())
        let splitTwo = timeStamp.split('_')
        let dateArray = [Number(splitTwo[2])]; //year
        dateArray[1] = (Number(new Date(splitTwo[0].split('-')[0] + " 1, 2012").toLocaleDateString('en', { month: '2-digit' })) - 1)//month
        dateArray[2] = Number(splitTwo[0].split('-')[1]) //day
        let time = splitTwo[1].split("-");
        date = new Date(dateArray[0], dateArray[1], dateArray[2], Number(time[0]), Number(time[1]), Number(time[2]));
        return date;
    }

    constructor(_certification: any, _customfields: any) {
        this.certification = _certification
        this.customFields = _customfields
    }
}